import { useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { getApiErrorMessage } from '@/shared/api/axios';
import { useDeleteMonthlyInput, useMonthlyInputs, usePayrollEmployees, useSaveMonthlyInput, type PayrollAccess, type PayslipLine } from './api';

const inr = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function LinesEditor({ title, lines, onChange }: { title: string; lines: { label: string; amount: string }[]; onChange: (lines: { label: string; amount: string }[]) => void }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">{title}</h4>
        <Button size="sm" variant="ghost" onClick={() => onChange([...lines, { label: '', amount: '' }])}><Plus className="mr-1 h-3.5 w-3.5" /> Add line</Button>
      </div>
      {lines.length === 0 && <p className="text-xs text-on-surface-variant">None.</p>}
      {lines.map((line, i) => (
        <div key={i} className="flex gap-2">
          <Input placeholder="Label (e.g. Basic Pay)" value={line.label} onChange={(e) => onChange(lines.map((l, j) => (j === i ? { ...l, label: e.target.value } : l)))} />
          <Input placeholder="Amount" type="number" min={0} className="w-36" value={line.amount} onChange={(e) => onChange(lines.map((l, j) => (j === i ? { ...l, amount: e.target.value } : l)))} />
          <Button size="sm" variant="ghost" onClick={() => onChange(lines.filter((_, j) => j !== i))} title="Remove"><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
}

const toRows = (lines: PayslipLine[]) => lines.map((l) => ({ label: l.label, amount: String(l.amount) }));
const toLines = (rows: { label: string; amount: string }[]): PayslipLine[] => rows.filter((r) => r.label.trim() || r.amount).map((r) => ({ label: r.label.trim(), amount: Number(r.amount) }));

/** Lines HR types for one person and month. When they exist, they are used instead of the salary structure. */
export function MonthlyLinesTab({ access }: { access: PayrollAccess }) {
  const [month, setMonth] = useState(format(new Date(), 'yyyy-MM'));
  const { data: inputs } = useMonthlyInputs(month);
  const { data: employees } = usePayrollEmployees();
  const save = useSaveMonthlyInput();
  const remove = useDeleteMonthlyInput();
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [earnings, setEarnings] = useState<{ label: string; amount: string }[]>([]);
  const [deductions, setDeductions] = useState<{ label: string; amount: string }[]>([]);
  const [notes, setNotes] = useState('');

  const edit = (id: string, e: PayslipLine[], d: PayslipLine[], n: string) => { setUserId(id); setEarnings(toRows(e)); setDeductions(toRows(d)); setNotes(n); setOpen(true); };
  const submit = async () => {
    try {
      await save.mutateAsync({ user_id: userId, month_year: month, earnings: toLines(earnings), deductions: toLines(deductions), notes });
      toast.success('Saved.');
      setOpen(false);
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />
          <p className="mt-1 text-xs text-on-surface-variant">Type a person's pay for the month yourself (for example an advance, a one-off allowance, or a different salary). It is used instead of their salary structure when payslips are generated.</p>
        </div>
        {access.canManage && <Button onClick={() => edit('', [], [], '')}><Plus className="mr-1 h-4 w-4" /> Add for an employee</Button>}
      </div>
      {!inputs?.length ? <p className="p-8 text-center text-on-surface-variant">Nothing typed for {month}.</p> : (
        <table className="w-full text-left text-sm">
          <thead className="bg-surface-variant text-xs uppercase text-on-surface-variant"><tr><th className="px-4 py-3 font-medium">Employee</th><th className="px-4 py-3 font-medium">Earnings</th><th className="px-4 py-3 font-medium">Deductions</th><th className="px-4 py-3" /></tr></thead>
          <tbody className="divide-y divide-outline/50">
            {inputs.map((i) => (
              <tr key={i.id}>
                <td className="px-4 py-3"><div className="font-medium">{i.user.display_name}</div><div className="text-xs text-on-surface-variant">{i.user.department}</div></td>
                <td className="px-4 py-3 text-xs">{i.earnings.map((l) => `${l.label} ${inr(l.amount)}`).join(', ') || '-'}</td>
                <td className="px-4 py-3 text-xs">{i.deductions.map((l) => `${l.label} ${inr(l.amount)}`).join(', ') || '-'}</td>
                <td className="space-x-1 whitespace-nowrap px-4 py-3 text-right">
                  {access.canManage && <>
                    <Button size="sm" variant="outline" onClick={() => edit(i.user_id, i.earnings, i.deductions, i.notes)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => remove.mutateAsync(i.id).then(() => toast.success('Removed.')).catch((err) => toast.error(getApiErrorMessage(err)))}><Trash2 className="h-4 w-4" /></Button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={`Lines for ${month}`} size="lg" footer={
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} disabled={!userId || save.isPending}>Save</Button></div>
      }>
        <div className="max-h-[60vh] space-y-4 overflow-auto py-2">
          <Select label="Employee" value={userId} placeholder="Choose an employee" options={(employees ?? []).map((e) => ({ value: e.id, label: e.display_name }))} onChange={(e) => setUserId(e.target.value)} />
          <LinesEditor title="Earnings" lines={earnings} onChange={setEarnings} />
          <LinesEditor title="Deductions" lines={deductions} onChange={setDeductions} />
          <Input label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </Modal>
    </div>
  );
}
